import { useState, useEffect, useRef } from "react";
import { useLanguage } from "@/i18n/index";

/**
 * YouTube Player Component - Embedded video player without external redirect
 * Tracks video interactions for educational analytics
 */
export default function YouTubePlayer({ 
  videoId, 
  title,
  onVideoStart,
  onVideoEnd,
  className = ""
}) {
  const { t } = useLanguage();
  const iframeRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasStarted, setHasStarted] = useState(false);
  const [error, setError] = useState(null);

  // Generate embed URL with parameters for better UX
  const embedUrl = videoId 
    ? `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&showinfo=0&enablejsapi=1&origin=${window.location.origin}`
    : null;

  useEffect(() => {
    // Reset state when video changes
    setIsLoading(true);
    setHasStarted(false);
    setError(null);
  }, [videoId]);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setError("Impossible de charger la vidéo");
    setIsLoading(false);
  };

  // Track when user clicks play (approximate - we can't detect exact play)
  const handleContainerClick = () => {
    if (!hasStarted && onVideoStart) {
      setHasStarted(true);
      onVideoStart();
    }
  };

  if (!videoId) {
    return (
      <div className={`bg-slate-100 rounded-2xl flex items-center justify-center ${className}`} style={{ aspectRatio: "16/9" }}>
        <p className="text-slate-400">Aucune vidéo disponible</p>
      </div>
    );
  }

  return (
    <div 
      className={`relative rounded-2xl overflow-hidden bg-black ${className}`}
      style={{ aspectRatio: "16/9" }}
      onClick={handleContainerClick}
    >
      {/* Loading spinner */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
          <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
          <div className="text-center text-white">
            <p className="text-red-400 mb-2">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-slate-700 rounded-lg hover:bg-slate-600"
            >
              Réessayer
            </button>
          </div>
        </div>
      )}

      {/* YouTube iframe */}
      <iframe
        ref={iframeRef}
        src={embedUrl}
        title={title || "Vidéo éducative"}
        className="w-full h-full"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        onLoad={handleIframeLoad}
        onError={handleIframeError}
      />
    </div>
  );
}
